import { createContext, useContext } from "react";

/**
 * The single time domain and playhead the whole stacked timeline shares.
 *
 * Lives apart from the provider component so consuming rows import a hook, not a
 * module that also exports a component — see `LiveTimelineDomainProvider` for
 * why the domain is computed once rather than per row.
 */
export interface LiveTimelineDomain {
  /** The visible [min, max] every stacked row must share. Null outside live mode. */
  domain: { min: number; max: number } | null;
  /** The live cursor, so every row draws its playhead at the same instant. */
  cursorMs: number | null;
}

export const LiveTimelineDomainContext = createContext<LiveTimelineDomain>({
  domain: null,
  cursorMs: null,
});

/**
 * Falls back to `{ null, null }` outside a provider, which makes a row compute
 * its own domain exactly as it did before — so the stack degrades rather than
 * breaks if a row is ever rendered on its own.
 */
export const useLiveTimelineDomainContext = (): LiveTimelineDomain =>
  useContext(LiveTimelineDomainContext);
